import { ReadonlyVec3 } from 'gl-matrix';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    ActiveEditTarget,
    useActiveEditTarget,
} from '~/hooks/target/useActiveEditTarget';
import type { ContourEditBlocker } from '~/domain/dicom/contourEditBlocker';
import { ROI_POINT_TYPES } from '~/domain/dicom/dicomstructureset';
import type { Environment } from '~/engine/data/Environment';
import { writeRefusal } from '~/engine/tool/tools/contouring-tools/editableView';
import { paneImageOf } from '~/engine/view/paneImage';
import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { localization } from '~/i18n';
import { uncommittedPointCoordinatesState } from '~/state/atoms/structureSet/roiAtoms';
import { selectedToolState, Tools } from '~/state/atoms/toolsAtoms';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import { useChangeVersion } from '~/viewportGrid/adapters/useChangeVersion';
import { planeViewOf } from '../ports/PlaneViewRenderAdapter';
import TilePoiInteraction from './TilePoiInteraction';
import TilePoiRefusalLayer from './TilePoiRefusalLayer';

interface TilePoiInteractionMountProps {
    renderer: RenderAdapter;
}

/** The part of a tile the resolver reads. */
export interface PoiTile {
    /** The Environment the tile's view shows. */
    environment: Environment | undefined;
    /**
     * Whether the tile draws its Environment's primary image, alone or
     * blended.
     */
    drawsPrimaryImage: boolean;
}

export type PoiInteractionState =
    | {
          kind: 'interactive';
          selectedRoiNumber: number;
          isReadyToPlace: boolean;
          isMovable: boolean;
          selectedPointMm: ReadonlyVec3 | null;
          editBlocker: ContourEditBlocker | undefined;
      }
    | {
          kind: 'refused';
          reason: string;
      };

/**
 * Whether/how a tile can place or move the active point ROI. Interaction is
 * gated to the editable tile: the one whose Environment IS the active edit
 * target's, the same object identity the engine write gate uses, and that
 * draws the Environment's primary image, the image the point is placed on.
 * POI writes bypass the engine gate, so this check is the only thing keeping
 * a place or move off another dataset.
 *
 * With poi-move armed, a tile of another Environment is refused with the
 * reason. A tile of the edit target that draws only the fused secondary
 * image is refused wherever it would otherwise place or move the point; the
 * secondary-only refusal names the way out and is read in the viewer's
 * language when the tile is resolved. A placed point ROI moves only while
 * poi-move is armed and the ROI is visible, so a point the user cannot see
 * is never moved. While the edit target's image is not whole, a tile of it
 * is refused wherever it would otherwise place or move the point, with
 * `imageNotWholeReason`, the reason the contouring tools give for the same
 * image. Returns null (inert) for every other case.
 *
 * @param imageNotWholeReason The localised reason the edit target's image
 *   is still loading or ended short; undefined when it is whole.
 */
export function resolveTilePoiInteraction(
    tile: PoiTile,
    target: ActiveEditTarget,
    selectedTool: Tools,
    imageNotWholeReason: string | undefined
): PoiInteractionState | null {
    const selectedRoi = target.structureSet?.roiArray.find(
        (roi) => roi.roiNumber === target.roiNumber
    );
    if (
        !selectedRoi ||
        !ROI_POINT_TYPES.includes(selectedRoi.interpretedType)
    ) {
        return null;
    }
    const poiMoveActive = selectedTool === 'poi-move';
    const tileRefusal = writeRefusal(tile, target.environment);
    if (tileRefusal !== undefined) {
        return poiMoveActive ? { kind: 'refused', reason: tileRefusal } : null;
    }
    const selectedPointMm = selectedRoi.pointCoordinatesMm;
    const isReadyToPlace = selectedPointMm === null;
    const isMovable =
        poiMoveActive && selectedPointMm !== null && selectedRoi.visible;
    if (!isReadyToPlace && !isMovable) {
        return null;
    }
    if (imageNotWholeReason !== undefined) {
        return { kind: 'refused', reason: imageNotWholeReason };
    }
    if (!tile.drawsPrimaryImage) {
        return {
            kind: 'refused',
            reason: localization.t('poi.placement.primaryImageOnly'),
        };
    }
    return {
        kind: 'interactive',
        selectedRoiNumber: target.roiNumber,
        isReadyToPlace,
        isMovable,
        selectedPointMm,
        editBlocker: selectedRoi.contourEditBlocker,
    };
}

/**
 * Wires the interactive POI layer to the active edit target for ONE tile,
 * re-projecting on every cursor move and display-scale change (a zoom, a
 * fit or a resize of the tile), so the move handle stays where the point
 * is drawn and a drag's release preview stays current. Renders nothing for a non-engine renderer (the mock adapters)
 * or a tile the resolver leaves inert.
 */
export default function TilePoiInteractionMount({
    renderer,
}: TilePoiInteractionMountProps) {
    useChangeVersion(renderer.getCursorSource());
    useChangeVersion(renderer.getDisplayScaleSource());
    const target = useActiveEditTarget();
    const selectedTool = useRecoilValue(selectedToolState);
    const setUncommittedPointCoordinates = useSetRecoilState(
        uncommittedPointCoordinatesState
    );
    const imageGate = useImageCompletenessGate(
        target.environment?.image?.parent
    );

    const view = planeViewOf(renderer);
    const state = view
        ? resolveTilePoiInteraction(
              {
                  environment: view.environment,
                  drawsPrimaryImage: paneImageOf(view).kind === 'primary',
              },
              target,
              selectedTool,
              imageGate.reason
          )
        : null;
    if (!view || !state) {
        return null;
    }
    if (state.kind === 'refused') {
        return (
            <TilePoiRefusalLayer reason={state.reason} canvas={view.canvas} />
        );
    }
    return (
        <TilePoiInteraction
            view={view}
            selectedRoiNumber={state.selectedRoiNumber}
            isReadyToPlace={state.isReadyToPlace}
            isMovable={state.isMovable}
            selectedPointMm={state.selectedPointMm}
            editBlocker={state.editBlocker}
            onReleasePreview={setUncommittedPointCoordinates}
        />
    );
}
